import React, { useState, useEffect } from 'react';
import { Card, Toolbar, Grid, CardContent, Button, Typography, Dialog, DialogContent, Chip, FormControl, FormControlLabel, MenuItem, IconButton } from '@mui/material';
import SearchBox from '../../components/searchbox';
import DropZone from '../files/dropzone';
import axios from 'axios';
import Config from '../../config';
import DownloadButton from '../../components/download_button';
import isEqual from 'lodash.isequal';
import { SimpleRte } from '../../components/EnhancedRte';
import { Form, Field } from 'react-final-form';
import Switch from '../../components/switch';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import TextField from '../../components/text_field';
import SelectFieldBase from '../../components/select_field';
import { LabelledSwitch as SwitchField } from '../../components/switch';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmDialog from '../../components/confirm_dialog';
import ExtendedTextField from '../../components/text_field';
import ExtendedSelectBase from '../../components/select';
import Helpers from '../../helpers';
import {
    OnboardingFormSettings,
    OnboardingReminderSettings,
    TestCaseGroupsState,
    OnboardingAgreement,
    FieldTypeOption,
} from './onboarding_settings.types';

export type { OnboardingFormSettings, OnboardingReminderSettings, TestCaseGroupsState };

// ExtendedSelect is a JS component wrapped in forwardRef; its inferred type
// only exposes RefAttributes, so cast to ComponentType<any> to allow the
// variant/options/onChange/style props (same pattern as supplier_onboarding.tsx).
const ExtendedSelect = ExtendedSelectBase as React.ComponentType<any>;
// SelectField's destructured-parameter shape is inferred with meta/label/
// formControlProps/shrink/variant all "required"; ConfirmSelectField below
// only ever provides a subset via a generic spread, which a JSX spread's
// index-signature type doesn't statically prove satisfies. Same ComponentType<any>
// relaxation as ExtendedSelect above.
const SelectField = SelectFieldBase as React.ComponentType<any>;

interface FastSelectProps {
    input: { value?: any; onChange: (value: any) => void };
    style?: React.CSSProperties;
    disabled?: boolean;
    [key: string]: any;
}

const FastSelect = (props: FastSelectProps) => {
    let {input, style, disabled, ...otherProps} = props;
    return <ExtendedSelect controlled value={input.value} onChange={input.onChange} {...otherProps} style={{placeSelf: 'inherit', ...style}}/>;
};

const fieldTypes: FieldTypeOption[] = [
    {label: "None / not used", value: "none"},
    {label: "Single line text / email address", value: "text"},
    {label: "Single-select", value: "select"},
    {label: "Multi-select", value: "multiselect"},
];

const scheduleOptions: FieldTypeOption[] = [
    {label: 'every 3 days', value: '0 12 */3 * *'},
    {label: 'every week', value: '0 12 * * 1'},
    {label: 'every 2 weeks', value: '0 12 1,15 * *'}
];

interface ConfirmSelectFieldProps {
    // name/value/onChange are all required (not optional) to match
    // SelectField's (select_field.js) inferred expectation — react-final-form
    // always populates all three at runtime anyway.
    input: { name: any; value: any; onChange: (e: any) => void; [key: string]: any };
    onChange: (onChange: (e: any) => void, e: any, oldValue: any) => void;
    [key: string]: any;
}

const ConfirmSelectField = (props: ConfirmSelectFieldProps) => {
    let {input: {...input}, onChange, ...otherProps} = props;
    input.onChange = (e: any) => onChange(props.input.onChange, e, input.value);
    return <SelectField input={input} {...otherProps}/>;
};

interface OnboardingSettingsProps {
    setTitle?: (title: string, includeInRecents?: boolean) => void;
    isRetailer?: boolean;
    isBillable?: boolean;
    history?: any;
    location?: any;
    match?: any;
}

interface OnboardingSettingsState {
    disableAgreementSave: boolean;
    showError: boolean;
    savingAgreement: boolean;
    tags: string[];
    loadedTags: string[];
    filteredTags: string[];
    tagTerm: string;
    stageMessage: string;
    productionMessage: string;
    form: Partial<OnboardingFormSettings>;
    showConfirm: boolean;
    testCases: TestCaseGroupsState;
    groupTerm: string;
    reminders?: Partial<OnboardingReminderSettings>;
    latestAgreement?: OnboardingAgreement | null;
    agreementEditable?: boolean;
    hasAgreement?: boolean;
    agreementFile?: File | null;
    error?: string | string[];
    confirmContent?: string;
    handleConfirm?: () => void;
    [key: string]: unknown;
}

const filterTags = (tags: string[], term: string) => (tags || []).filter(r => !term || r.toLowerCase().indexOf(term.toLowerCase()) > -1);

const OnboardingSettings = (props: OnboardingSettingsProps) => {
    const [state, setState] = useState<OnboardingSettingsState>({
        disableAgreementSave: false, showError: false, savingAgreement: false, tags: [], loadedTags: [], filteredTags: [],
        tagTerm: '', stageMessage: '', productionMessage: '', form: {},
        showConfirm: false, testCases: {Enabled: false, Groups: [], Details: []}, groupTerm: ''
    });

    // Faithful stand-in for class `this.setState(partial)`'s shallow merge; the
    // functional updater preserves merge semantics and supports the dynamic
    // keys (e.g. `${environment}Changed`) the class relied on.
    const mergeState = (patch: Partial<OnboardingSettingsState> | ((prev: OnboardingSettingsState) => Partial<OnboardingSettingsState>)) =>
        setState(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));

    const loadConfig = async () => {
        let settings = (await axios.get(Config.oauth + '/integration/getconfig')).data;
        let tags = settings.Tags || [];
        let productionMessage = settings.ProductionMessage || '';
        let stageMessage = settings.StageMessage || '';
        let reminders: OnboardingReminderSettings = {
            ResendOnboardingInvite: settings.ResendOnboardingInvite || false,
            ResendInviteAfterDays: settings.ResendInviteAfterDays || '',
            RepeatOnboardingInvite: settings.RepeatOnboardingInvite || false,
            OnboardingInviteSchedule: settings.OnboardingInviteSchedule || '',
            SendOnboardingReminder: settings.SendOnboardingReminder || false,
            SendReminderAfterDays: settings.SendReminderAfterDays || '',
            RepeatOnboardingReminder: settings.RepeatOnboardingReminder || false,
            OnboardingReminderSchedule: settings.OnboardingReminderSchedule || '',
         };
        let form: OnboardingFormSettings = {AutoAccept: settings.AutoAccept || false, ResetStartOnSubmit: settings.ResetStartOnSubmit || false, CustomFields: settings.CustomFields || []};
        for(var i = 1; i <= 15; i++){
            let id = "CustomField" + i;
            if(form.CustomFields.filter(r => r.Id === id).length === 0){
                form.CustomFields.push({Id: id, Type: 'none'});
            }
        }
        mergeState(prev => ({loadedTags: tags, tags: tags, filteredTags: filterTags(tags, prev.tagTerm), productionMessage, stageMessage, form, reminders}));
    };
    const loadAgreement = async () => {
        try{
            let latestAgreement = (await axios.get(Config.oauth + '/integration/getagreement')).data;
            mergeState({latestAgreement, agreementEditable: true, hasAgreement: true});
        }catch(e){
            mergeState({latestAgreement: null, agreementEditable: true, hasAgreement: false});
        }
    };
    const loadTestCases = async () => {
        let testCases = (await axios.get(Config.api + '/odata/Company/Functions.GetTestCaseGroups')).data;
        testCases.Details = testCases.Details.sort((a: any, b: any) => a.Name.localeCompare(b.Name));
        mergeState({testCases});
    };
    const saveAgreement = async () => {
        let files = [state.agreementFile];
        if(!files || !files[0]){
            return;
        }
        try{
            mergeState({savingAgreement: true});
            let formData = new FormData();
            formData.append("file", files[0]);
            await axios.post(Config.oauth + '/integration/setagreement', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
            });
            await loadAgreement();
            mergeState({savingAgreement: false, agreementFile: null});
        }catch(e: any){
            let error = 'Unexpected error.';
            if(e.response && e.response.data && e.response.data.error){
                error = e.response.data.error;
            }
            mergeState({showError: true, error, savingAgreement: false});
        }
    };
    const createTag = () => {
        let tag = (state.tagTerm || '').trim();
        if(!tag || state.tags.indexOf(tag) > -1){
            return;
        }
        let tags = [...state.tags, tag];
        mergeState({tags, filteredTags: filterTags(tags, ''), tagTerm: ''});
    };
    const deleteTag = (tag: string) => {
        let tags = state.tags.filter(r => r !== tag);
        mergeState({tags, filteredTags: filterTags(tags, state.tagTerm)});
    };
    const saveTags = async () => {
        try{
            let data = {Tags: state.tags};
            await axios.post(Config.oauth + '/integration/updatetags', data);
            await loadConfig();
        }catch(e: any){
            let error = 'Unexpected error.';
            if(e.response && e.response.data && e.response.data.error){
                error = e.response.data.error;
            }
            mergeState({showError: true, error});
        }
    };
    const updateTagTerm = (e: any) => {
        let value = e.currentTarget.value;
        if(state.tagTerm !== value){
            mergeState({tagTerm: value, filteredTags: filterTags(state.tags, value)});
        }
    };
    const updateGroupTerm = (e: any) => {
        let value = e.currentTarget.value;
        if(state.groupTerm !== value){
            mergeState({groupTerm: value});
        }
    };
    const saveMessage = async (rteValue: string, environment: string) => {
        try{
            let html = rteValue;
            let frag = document.createElement("div");
            frag.innerHTML = html;
            html = !(!frag.innerText) ? html : '';
            let data = {Content: html, Environment: environment};
            await axios.post(Config.oauth + '/integration/updatemessage', data);
            await loadConfig();
            mergeState({[environment + 'Changed']: false});
        }catch(e: any){
            let error = 'Unexpected error.';
            if(e.response && e.response.data && e.response.data.error){
                error = e.response.data.error;
            }
            mergeState({showError: true, error});
        }
    };
    const saveForm = async (data: any) => {
        try{
            await axios.post(Config.oauth + '/integration/updateform', data);
            await loadConfig();
        }catch(e: any){
            let error = 'Unexpected error.';
            if(e.response && e.response.data && e.response.data.error){
                error = e.response.data.error;
            }
            mergeState({showError: true, error});
        }
    };
    const saveReminders = async (data: any) => {
        try{
            await axios.post(Config.oauth + '/integration/updatereminders', data);
            await loadConfig();
        }catch(e: any){
            let error = 'Unexpected error.';
            if(e.response && e.response.data && e.response.data.error){
                error = e.response.data.error;
            }
            mergeState({showError: true, error});
        }
    };
    const saveTestCases = async (data: any) => {
        try{
            await axios.post(Config.api + '/odata/Company/Functions.UpdateTestCaseGroups', data);
            await loadTestCases();
        }catch(e: any){
            let error = new Helpers().getApiErrors((e.response || {}).data);
            mergeState({showError: true, error});
        }
    };
    const newOptionValid = (value: string, fields: any): string | null | undefined => {
        if(!value){
            return null;
        }
        if(value.indexOf(',') > -1){
            return 'Commas are not allowed in options.';
        }
        if(fields && fields.value && fields.value.indexOf(value) > -1){
            return 'Duplicate options are not allowed.';
        }
    };
    const onTypeChange = (onChange: (e: any) => void, e: any, oldValue: any) => {
        let val = e.target.value;
        if(val === "none" && oldValue !== "none" && !(!oldValue)){
            mergeState({
                showConfirm: true,
                confirmContent: "Changing this field type to None/Not Used will delete this question and remove any existing supplier data. Are you sure you want to continue?",
                handleConfirm: () => onChange(e)
            });
            return;
        } else if ((oldValue === "select" || oldValue === "multiselect") && !(val === "select" || val === "multiselect")){
            mergeState({
                showConfirm: true,
                confirmContent: "Changing to this field type will remove all existing options. Are you sure you want to continue?",
                handleConfirm: () => onChange(e)
            });
            return;
        }
        onChange(e);
    };

    const removeGroup = (args: any[], formState: any, utils: any) => {
        if(args[2] === true){
            utils.changeValue(formState, 'Groups', (v: string[]) => v.filter(r => r !== args[0]));
            for(let i = 0; i < formState.formState.values.Details.length; i++){
                let details = formState.formState.values.Details[i];
                if(details.Groups && details.Groups.filter((r: string) => r === args[0]).length > 0){
                    utils.changeValue(formState, `Details[${i}].Groups`, (v: string[]) => v.filter(r => r !== args[0]));
                }
            }
        } else {
            mergeState({
                showConfirm: true,
                confirmContent: "Deleting this group will automatically unassign test cases associated with it. Suppliers that were a part of this test group will now see all test cases. Are you sure you want to continue?",
                handleConfirm: () => args[1](args[0], args[1], true)
            });
        }
    };

    useEffect(() => {
        const init = async () => {
            if(props.setTitle) {
                props.setTitle('Onb. Settings', true);
            }
            await loadConfig();
            await loadAgreement();
            if(!Config.production){
                await loadTestCases();
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container spacing={2}>
            <Grid size={{ sm: 12, md: 12, lg: 12 }} style={{display: 'flex'}}>
                <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Toolbar className='lbtoolbar'>Jump To</Toolbar>
                <CardContent style={{textAlign: 'left', display: 'flex', gap: '1em'}}>
                    <Button component='a' variant='outlined' href='#tags'>Tags</Button>
                    <Button component='a' variant='outlined' href='#vendorAgreement'>Supplier Agreement</Button>
                    {!Config.production && <Button component='a' variant='outlined' href='#stageMessage'>Stage Message</Button>}
                    <Button component='a' variant='outlined' href='#productionMessage'>Production Message</Button>
                    <Button component='a' variant='outlined' href='#onboardingForm'>Onboarding Form</Button>
                    <Button component='a' variant='outlined' href='#reminders'>Supplier Reminders</Button>
                    {!Config.production && <Button component='a' variant='outlined' href='#testcases'>Test Cases</Button>}
                </CardContent>
                </Card>
            </Grid>
            <Grid id="tags" size={12} style={{display: 'flex'}}>
                <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Toolbar className='lbtoolbar'>Tags</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                        <Typography>Add and manage supplier tags. Tags can be seen from the Onboarding page to provide classification for suppliers based on things like category, merchandiser, etc.</Typography>
                        <div style={{display: 'flex', gap: '1em', marginTop: '1em'}}>
                            <SearchBox inputStyle={{width: '15em'}} term={state.tagTerm} handleKeyUp={updateTagTerm} onEnter={updateTagTerm} handleChange={updateTagTerm} placeholder='Search or create tags'/>
                            <Button variant='outlined' onClick={createTag} disabled={!state.tagTerm}>Create</Button>
                        </div>
                        <div style={{marginTop: '1em', marginBottom: '1em', display: 'flex', gap: '1em'}}>
                            {state.filteredTags.map(r => <Chip key={r} label={r} onDelete={() => deleteTag(r)} />)}
                        </div>
                        <DownloadButton variant='contained' color='primary' onClick={saveTags} disabled={isEqual(state.tags, state.loadedTags)}>Save</DownloadButton>
                    </CardContent>
                </Card>
            </Grid>
            <Grid id="vendorAgreement" size={12} style={{display: 'flex'}}>
                <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Toolbar className='lbtoolbar'>Supplier Agreement</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                        <Typography>Upload a PDF file of the agreement you would like your suppliers to review and sign from the Onboarding form. Only 1 file is supported.</Typography>
                        <DropZone accept=".pdf" style={{width: '100%', height: '10em', marginTop: '1em', marginBottom: '1em'}} onDrop={(files: File[]) => {mergeState({agreementFile: files[0]})}}>
                        {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }: any) => {
                            if (isDragAccept || isDragReject) {
                              return "Drop here to upload this file.";
                            }
                            return state.agreementFile ? state.agreementFile.name : 'Drop a PDF file here or click to choose a file.';
                          }}
                        </DropZone>
                        <Button component="a" target="_blank" href={Config.oauth + "/integration/downloadagreement/" + ((state.latestAgreement || {}).Id || "")} variant='outlined' disabled={!state.hasAgreement} style={{minWidth: '10em', marginRight: '1em'}}>View Current Agreement</Button>
                        <DownloadButton variant='contained' color='primary' onClick={saveAgreement} disabled={!state.agreementFile}>Save</DownloadButton>
                    </CardContent>
                </Card>
            </Grid>
            {!Config.production && <Grid id="stageMessage" size={12} style={{display: 'flex'}}>
                <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Toolbar className='lbtoolbar'>Stage Message</Toolbar>
                <CardContent style={{textAlign: 'left'}}>
                <Typography>Share a message with your suppliers from the stage dashboard. Examples of what you may want to include are: non-Logicbroker onboarding tasks, alerts and additional directions.</Typography>
                    <div style={{height: '200px', display: 'flex', marginTop: '1em', marginBottom: '1em'}}>
                        <SimpleRte className='textEditor' value={state.stageMessage} onChange={(html: string, isEmpty: boolean) => mergeState({stageMessage: html, stageChanged: true})}/>
                    </div>
                    <DownloadButton disabled={!state.stageChanged} variant='contained' color='primary' onClick={() => saveMessage(state.stageMessage, 'stage')}>Save</DownloadButton>
                    </CardContent>
                </Card>
            </Grid>}
            <Grid id="productionMessage" size={12} style={{display: 'flex'}}>
                <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Toolbar className='lbtoolbar'>Production Message</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                        <Typography>Share a message with your suppliers from the production dashboard. Examples of what you may want to include are: non-Logicbroker onboarding tasks, alerts and additional directions.</Typography>
                        <div style={{height: '200px', display: 'flex', marginTop: '1em', marginBottom: '1em'}}>
                            <SimpleRte className='textEditor' value={state.productionMessage} onChange={(html: string, isEmpty: boolean) => mergeState({productionMessage: html, productionChanged: true})}/>
                        </div>
                        <DownloadButton disabled={!state.productionChanged} variant='contained' color='primary' onClick={() => saveMessage(state.productionMessage, 'production')}>Save</DownloadButton>
                    </CardContent>
                </Card>
            </Grid>
            <Grid id="onboardingForm" size={12} style={{display: 'flex'}}>
                <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Toolbar className='lbtoolbar'>Onboarding Form</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                    <Form onSubmit={saveForm}
                        mutators={{...arrayMutators}}
                        initialValues={state.form}
                        render={({ handleSubmit, pristine, invalid, form }) => (
                          <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                            <Typography variant='h6'>Settings</Typography>
                          <FormControl>
                                <FormControlLabel label='Auto-accept onboarding form' control={<Field type="checkbox" component={Switch as any} name="AutoAccept"/>}/>
                                <FormControlLabel label='Reset start date when supplier completes the onboarding form' control={<Field type="checkbox" component={Switch as any} name="ResetStartOnSubmit"/>}/>
                          </FormControl>
                          <Typography variant='h6' style={{marginTop: '1rem'}}>Custom Questions</Typography>
                          <Typography>Add custom questions to your supplier's onboarding form.</Typography>
                          <FieldArray name='CustomFields'>
                        {({fields}) =>
                            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem'}}>
                            {fields.map((name, index) => <div key={name} style={{maxWidth: '50rem', borderRadius: '4px', border: '1px solid #aaa', backgroundColor: "#f4f4f4", padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                                     <Typography style={{fontWeight: 'bold', marginTop: '0.5rem', marginBottom: '0.5rem'}}>Question #{fields.value[index!].Id.replace("CustomField", "")}</Typography>
                                    <Field style={{width: '100%'}} component={ConfirmSelectField as any} onChange={onTypeChange} label='Question type' name={`${name}.Type`}>
                                        {fieldTypes.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                    </Field>
                                    {fields.value[index!].Type !== "none" && <>
                                    <div><Field component={TextField} style={{width: '100%'}} label='Field name' name={`${name}.Name`} validate={v => v ? undefined : 'Field name is required.'}/></div>
                                    <div><Field component={TextField} style={{width: '100%'}} label='Description' name={`${name}.Description`}/></div>
                                    <div style={{width: '100%', textAlign: 'right'}}><Field component={SwitchField} label='Required' name={`${name}.Required`} type="checkbox"/></div>
                                    {(fields.value[index!].Type === "select" || fields.value[index!].Type === "multiselect") && <>
                                    <Typography style={{fontWeight: 'bold', marginTop: '0.5rem', marginBottom: '0.5rem'}}>Options</Typography>
                                    <FieldArray name={`${name}.Options`}>
                                    {({fields: optionFields}) => <div>
                                        {optionFields.map((name, index) => <div key={name} style={{borderBottom: '1px solid #aaa', padding: '0.25rem', display: 'flex'}}>
                                            <span style={{flex: 1}}>{optionFields.value[index!]}</span>
                                            <IconButton
                                                onClick={() => optionFields.remove(index!)}
                                                style={{padding: '2px'}}
                                                size="large"><CloseIcon style={{height: '0.75em', width: '0.75em'}}/></IconButton>
                                        </div>)}
                                        <div style={{marginTop: '0.5rem'}}>
                                            <Field component={TextField} name={`${name}.option`} style={{marginRight: '2em', verticalAlign: 'bottom', minWidth: '20em'}} placeholder="New option" validate={v => newOptionValid(v, optionFields)} alwaysShowError={true}/>
                                            <Button variant='outlined' onClick={() => {optionFields.push(fields.value[index!].option); form.change(`${name}.option`, '');}} disabled={!fields.value[index!].option || !(!newOptionValid(fields.value[index!].option, optionFields))}>Add option</Button>
                                        </div>
                                    </div>}
                                    </FieldArray>
                                    </>}
                                    </>}
                                </div>)}
                            </div>
                        }
                        </FieldArray>
                        <div style={{marginTop: '0.5rem'}}><DownloadButton disabled={invalid || pristine} variant='contained' color='primary' onClick={handleSubmit}>Save</DownloadButton></div>
                        </form>)}
                        />
                    </CardContent>
                </Card>
            </Grid>
            <Grid id="reminders" size={12} style={{display: 'flex'}}>
                <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Toolbar className='lbtoolbar'>Supplier Reminders</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                    <Form onSubmit={saveReminders}
                        mutators={{...arrayMutators}}
                        initialValues={state.reminders}
                        render={({ handleSubmit, pristine, invalid, form, values }) => (
                          <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                            <Typography>Configure automated reminder notifications to be sent to suppliers by email when their status has not changed. After 4 attempts, we will stop further outreach and you will see an alert on the supplier's page. At this point, please reach out to the supplier directly to discuss the partnership.</Typography>
                            <Typography variant='h6' style={{marginTop: '1rem'}}>Resend Invitation</Typography>
                            <div>
                                <div>
                                    <Field component={SwitchField} name='ResendOnboardingInvite' type='checkbox' label={<div style={{display: 'flex', alignItems: 'baseline'}}>Resend onboarding invitation after <Field component={ExtendedTextField} validate={(v, allValues: any) => (allValues.ResendOnboardingInvite && !v) || v < 1 || v > 20 ? true : undefined} inputProps={{readOnly: !values.ResendOnboardingInvite, max: 20, min: 1}} style={{margin: '0 0.5em 0 0.5em', width: '4em'}} name='ResendInviteAfterDays' type='number'/> days of account in an 'Invite Sent' status.</div>}/>
                                </div>
                                <div>
                                    <Field component={SwitchField} name='RepeatOnboardingInvite' type='checkbox' label={<div style={{display: 'flex', alignItems: 'baseline'}}>Repeat <Field component={SelectField as any} validate={(v, allValues: any) => allValues.RepeatOnboardingInvite && !v ? true : undefined} inputProps={{readOnly: !values.RepeatOnboardingInvite}} style={{margin: '0 0.5em 0 0.5em', width: '10em'}} name='OnboardingInviteSchedule'>{scheduleOptions.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}</Field></div>}/>
                                </div>
                            </div>
                            <Typography variant='h6' style={{marginTop: '1rem'}}>Onboarding Reminder</Typography>
                            <div>
                                <div>
                                    <Field component={SwitchField} name='SendOnboardingReminder' type='checkbox' label={<div style={{display: 'flex', alignItems: 'baseline'}}>Send reminder to complete onboarding steps after <Field component={ExtendedTextField} validate={(v, allValues: any) => allValues.SendOnboardingReminder && !v ? true : undefined} inputProps={{readOnly: !values.SendOnboardingReminder, max: 20, min: 1}} style={{margin: '0 0.5em 0 0.5em', width: '4em'}} name='SendReminderAfterDays' type='number'/> days of account in an 'Onboarding' status.</div>}/>
                                </div>
                                <div>
                                    <Field component={SwitchField} name='RepeatOnboardingReminder' type='checkbox' label={<div style={{display: 'flex', alignItems: 'baseline'}}>Repeat <Field component={SelectField as any} validate={(v, allValues: any) => (allValues.RepeatOnboardingReminder && !v) || v < 1 || v > 20 ? true : undefined} inputProps={{readOnly: !values.RepeatOnboardingReminder}} style={{margin: '0 0.5em 0 0.5em', width: '10em'}} name='OnboardingReminderSchedule'>{scheduleOptions.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}</Field></div>}/>
                                </div>
                            </div>
                            <div style={{marginTop: '0.5rem'}}><DownloadButton disabled={invalid || pristine} variant='contained' color='primary' onClick={handleSubmit}>Save</DownloadButton></div>
                          </form>)}
                        />
                    </CardContent>
                </Card>
            </Grid>
            {!Config.production && <Grid id="testcases" size={12} style={{display: 'flex'}}>
                <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Toolbar className='lbtoolbar'>Test Cases</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                    <Typography variant='h6'>Supplier-Specific Test Cases</Typography>
                    <Form onSubmit={saveTestCases}
                        mutators={{...arrayMutators, removeGroup}}
                        initialValues={state.testCases}
                        render={({ handleSubmit, pristine, invalid, form, values }) => (
                          <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                            <Typography>By default, all test cases will be visible to all suppliers. To enable test cases by supplier, toggle on the setting below and configure the test cases.</Typography>
                            <div>
                                <Field component={SwitchField} name='Enabled' type='checkbox' label={<div style={{display: 'flex', alignItems: 'baseline'}}>Enable test cases by supplier</div>}/>
                            </div>
                            {values.Enabled && <div>
                                <Typography variant='h6' style={{marginTop: '1rem'}}>Test Groups</Typography>
                                <Typography>Name each supplier test group - you will then use these to apply to each test case (ex. EDI Suppliers, Portal Suppliers, Parcel Suppliers, etc.).</Typography>
                                <FieldArray name="Groups">
                                    {({fields}) => <>
                                    <div style={{display: 'flex', gap: '1em', marginTop: '1em'}}>
                                        <SearchBox inputStyle={{width: '15em'}} term={state.groupTerm} handleKeyUp={updateGroupTerm} onEnter={updateGroupTerm} handleChange={updateGroupTerm} placeholder='Add test group'/>
                                        <Button variant='outlined' onClick={() => {fields.push(state.groupTerm); mergeState({groupTerm: ""});}} disabled={!state.groupTerm || values.Groups.filter((r: string) => r === state.groupTerm).length > 0}>Add</Button>
                                    </div>
                                    <div style={{marginTop: '1em', marginBottom: '1em', display: 'flex', gap: '1em'}}>
                                        {fields.map((name, index) => <Chip key={name} label={fields.value[index!]} onDelete={() => form.mutators.removeGroup(fields.value[index!], form.mutators.removeGroup)} />)}
                                    </div>
                                    </>}
                                </FieldArray>
                                <Typography variant='h6' style={{marginTop: '1rem'}}>Test Cases</Typography>
                                <Typography>Choose which test group(s) can see each test case. Please note that suppliers that are not assigned to a group will see all test cases by default.</Typography>
                                <FieldArray name="Details">
                                {({fields}) =>
                                    fields.map((name, index) => <div key={name} style={{maxWidth: '50rem', borderRadius: '4px', border: '1px solid #aaa', backgroundColor: "#f4f4f4", marginTop: '1rem', marginBottom: '1rem', padding: '0.5rem'}}>
                                            <Typography style={{fontWeight: 'bold', marginTop: '0.5rem', marginBottom: '0.5rem'}}>{fields.value[index!].Name}</Typography>
                                            <Typography>Supplier test groups that will see this test case:</Typography>
                                            <Field name={`${name}.Groups`} component={FastSelect as any} style={{width: '100%'}} placeholder='All suppliers not part of a test group' options={values.Groups.map((r: string) => ({label: r, value: r}))}/>
                                        </div>)
                                }
                                </FieldArray>
                            </div>}
                            <div style={{marginTop: '0.5rem'}}><DownloadButton disabled={invalid || pristine} variant='contained' color='primary' onClick={handleSubmit}>Save</DownloadButton></div>
                          </form>)}
                        />
                    </CardContent>
                </Card>
            </Grid>}
            <Dialog open={state.showError} onClose={() => {mergeState({showError: false})}}>
                <Toolbar className='lbtoolbar'>Error</Toolbar>
                <DialogContent><Typography>{state.error}</Typography></DialogContent>
            </Dialog>
            <ConfirmDialog open={state.showConfirm} onClose={() => mergeState({showConfirm: false})} message={state.confirmContent} onConfirm={state.handleConfirm}/>
        </Grid>
    );
};

export default OnboardingSettings;
export {
    FastSelect,
    fieldTypes,
    scheduleOptions,
    ConfirmSelectField
};
